import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ChevronDown } from 'lucide-react';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import type { StudioId } from '@/domain/types';
import DashboardKPITile from '@/components/DashboardKPITile';
import InfoTip from '@/components/InfoTip';
import { ReportBarChart } from '@/components/reporting/ReportChart';
import { REPORT_COLORS } from '@/components/reporting/reportChartTheme';
import { useFlashHighlight } from '@/hooks/useFlashHighlight';
import { aud } from '@/utils/format';
import {
  revenueByDay,
  revenueByStore,
  revenueByArtist,
  cashTimingAcrossStudios,
  merchSold,
  merchByCategory,
  merchSoldByDay,
  depositsReceivedIn,
  depositsReceivedByDay,
  depositPool,
  topupReport,
  previousWindowDays,
  dayKeyOf,
} from '@/domain/reportingEngine';
import type { TopupTrackState } from '@/domain/topupReminders';

function dayLabel(dayKey: string): string {
  return new Date(`${dayKey}T12:00:00+10:00`).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric' });
}

/** A human span for the selected range: a single day, or "1 Apr – 7 Apr". */
function spanLabel(days: readonly string[]): string {
  if (days.length === 0) return '';
  const fmt = (d: string) =>
    new Date(`${d}T12:00:00+10:00`).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  return days.length === 1 ? fmt(days[0]!) : `${fmt(days[0]!)} – ${fmt(days[days.length - 1]!)}`;
}

/** Percentage change vs the previous equal-length period; undefined when there is no base. */
function deltaPct(current: number, previous: number): number | undefined {
  if (previous <= 0) return undefined;
  return ((current - previous) / previous) * 100;
}

const AGING_META: Record<string, { label: string; bar: string; flag?: string }> = {
  pastValidity: { label: 'Past 12 months', bar: 'bg-blood', flag: 'action' },
  le30: { label: 'Expiring ≤ 30 days', bar: 'bg-blood', flag: 'action' },
  d31to90: { label: '31 – 90 days', bar: 'bg-gold-bright' },
  d91plus: { label: '91 days – 12 months', bar: 'bg-gold-dim' },
};

const TOPUP_STATE_META: Record<TopupTrackState, { label: string; bar: string; dot: string }> = {
  due: { label: 'Reminder due', bar: 'bg-gold-bright', dot: 'bg-gold-bright' },
  'final-due': { label: 'Final due · T-7', bar: 'bg-blood', dot: 'bg-blood' },
  waiting: { label: 'Waiting', bar: 'bg-gold-dim', dot: 'bg-gold-dim' },
  suppressed: { label: 'Suppressed · replied', bar: 'bg-mint', dot: 'bg-mint' },
};
const TOPUP_STATE_ORDER: TopupTrackState[] = ['due', 'final-due', 'waiting', 'suppressed'];
const TOPUP_DAYS_META: Record<string, { label: string; read: string; tone: string }> = {
  le7: { label: '0 – 7 days', read: 'Chase now — final reminders', tone: 'text-blood' },
  d8to21: { label: '8 – 21 days', read: 'On cadence', tone: 'text-cream-soft' },
  d22plus: { label: '22+ days', read: 'Plenty of runway', tone: 'text-cream-soft' },
};

interface Props {
  /** The selected date-range window for revenue, money-in and per-store totals. */
  days: readonly string[];
  /** Studio scope, or undefined for all studios. */
  studioId?: StudioId;
}

/**
 * Money tab (Prompt F). The ranged money *report* for the group: turnover split
 * ours-vs-artist over the range (trend + per-store, or per-artist when one studio
 * is filtered), the money-in views (deposits + merch, each visualised), the live
 * deposit-liability pool, and the settlement cash-timing strip. Deep-links down to
 * the Day sheet (per-artist daily detail) and Reconcile. All figures honour the
 * shell's range + studio filters, except the pool (a *now* liability snapshot) and
 * cash timing (a fixed rolling 7-day settlement view).
 */
export default function MoneyTab({ days, studioId }: Props) {
  const bookings = useBrainStore((s) => s.bookings);
  const salePayments = useBrainStore((s) => s.salePayments);
  const deposits = useBrainStore((s) => s.deposits);
  const artists = useBrainStore((s) => s.artists);
  const studios = useBrainStore((s) => s.studios);
  const merchItems = useBrainStore((s) => s.merchItems);
  const settlementLandings = useBrainStore((s) => s.settlementLandings);
  const clockIso = useSimulationStore((s) => s.clockIso);

  const [open, setOpen] = useState<'deposits' | 'merch' | null>(null);
  const flashing = useFlashHighlight(['stripe-webhook-success', 'booking:checked-out']);

  // Cash timing is a fixed 7-day settlement strip; end it on the range's last day.
  const stripEnd = days.length > 0 ? days[days.length - 1]! : dayKeyOf(clockIso);

  const sumRevenue = (rows: { oursAud: number; artistAud: number; totalAud: number }[]) =>
    rows.reduce(
      (acc, r) => ({ ours: acc.ours + r.oursAud, artist: acc.artist + r.artistAud, total: acc.total + r.totalAud }),
      { ours: 0, artist: 0, total: 0 },
    );

  const revenueTrend = useMemo(
    () =>
      revenueByDay(days, studioId).map((r) => ({
        day: dayLabel(r.dayIso),
        Ours: Math.round(r.oursAud),
        Artist: Math.round(r.artistAud),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [days, studioId, bookings, salePayments, artists],
  );

  const totals = useMemo(
    () => sumRevenue(revenueByDay(days, studioId)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [days, studioId, bookings, salePayments, artists],
  );
  const prevTotals = useMemo(
    () => sumRevenue(revenueByDay(previousWindowDays(days), studioId)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [days, studioId, bookings, salePayments, artists],
  );

  const perStore = useMemo(
    () => revenueByStore(days, studioId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [days, studioId, bookings, salePayments, artists, studios],
  );
  const perArtist = useMemo(
    () => (studioId ? revenueByArtist(days, studioId) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [days, studioId, bookings, salePayments, artists],
  );

  const timing = useMemo(
    () => cashTimingAcrossStudios(stripEnd, studioId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stripEnd, studioId, settlementLandings, studios],
  );

  const merch = useMemo(
    () => merchSold(days, studioId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [days, studioId, salePayments, studios, merchItems],
  );
  const merchCats = useMemo(
    () => merchByCategory(days, studioId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [days, studioId, salePayments, studios, merchItems],
  );
  const merchTrend = useMemo(
    () => merchSoldByDay(days, studioId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [days, studioId, salePayments, studios],
  );

  const depositsIn = useMemo(
    () => depositsReceivedIn(days, studioId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [days, studioId, deposits, studios],
  );
  const depTrend = useMemo(
    () => depositsReceivedByDay(days, studioId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [days, studioId, deposits, studios],
  );
  const pool = useMemo(
    () => depositPool(studioId, { rangeDays: days }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [days, studioId, deposits, bookings],
  );
  // Top-up book snapshot (a now figure, like the held pool) — state + urgency of
  // partial deposits still owing a balance.
  const topups = useMemo(
    () => topupReport(studioId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [studioId, deposits, bookings],
  );

  const timingChart = timing.map((d) => ({
    day: dayLabel(d.dayIso),
    Takings: Math.round(d.takingsAud),
    Landed: Math.round(d.landingAud),
  }));
  const hasTiming = timing.some((d) => d.takingsAud > 0 || d.landingAud > 0);

  const poolTotal = Math.max(1, pool.heldAud);
  const maxBucket = Math.max(1, ...pool.aging.map((a) => a.amountAud));
  const maxCat = Math.max(1, ...merchCats.map((c) => c.totalAud));
  const spanCaption = `${days.length} day${days.length === 1 ? '' : 's'} · ${spanLabel(days)}`;

  return (
    <div className="space-y-6 max-w-6xl" data-testid="reporting-money">
      {/* Totals across the selected range (dynamic label; deltas vs the previous period) */}
      <section>
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <h2 className="heading-serif text-lg text-cream">Totals</h2>
          <p className="font-inter text-[10px] text-cream-faint uppercase tracking-widest">{spanCaption}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
          <DashboardKPITile
            label="Turnover"
            value={aud(totals.total)}
            delta={deltaPct(totals.total, prevTotals.total)}
            caption={prevTotals.total > 0 ? 'vs previous period' : undefined}
            info="Every completed session in the range (all studios unless filtered). Each session's gross is its sale balance plus the deposit applied; merch and deposits-in are excluded. Ours + Artists add up to this."
          />
          <DashboardKPITile
            label="Ours (house)"
            value={aud(totals.ours)}
            accent
            delta={deltaPct(totals.ours, prevTotals.ours)}
            info="The studio's share of turnover after the artist commission split, from the commission engine."
          />
          <DashboardKPITile
            label="Artists"
            value={aud(totals.artist)}
            delta={deltaPct(totals.artist, prevTotals.artist)}
            info="The artists' share of turnover (their envelopes) after the commission split."
          />
        </div>
      </section>

      {/* Money in: deposits (received + held pool) and merch (sold + by category), each visualised */}
      <section className="space-y-3" data-testid="money-in">
        {/* Deposits */}
        <div className={clsx('panel p-4', flashing && 'report-flash')} data-testid="money-deposits">
          <h2 className="heading-serif text-lg text-cream mb-1">
            Deposits
            <InfoTip>
              Held money owed in ink, not revenue. Left: new deposits paid in the range (money-in, excludes checkout
              rollovers). Right: every deposit still outstanding right now, a live liability snapshot.
            </InfoTip>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-2">
            {/* Received in range (flow) */}
            <div>
              <p className="label-small-caps">Received in range</p>
              <button
                type="button"
                onClick={() => setOpen(open === 'deposits' ? null : 'deposits')}
                aria-expanded={open === 'deposits'}
                disabled={depositsIn.rows.length === 0}
                className="mt-1 w-full flex items-end justify-between text-left disabled:cursor-default"
              >
                <span className="font-cinzel text-3xl text-mint">{aud(depositsIn.totalAud)}</span>
                {depositsIn.rows.length > 0 ? (
                  <span className="font-inter text-[10px] text-cream-soft uppercase tracking-widest flex items-center gap-1">
                    {depositsIn.rows.length} in
                    <ChevronDown size={12} className={clsx('transition-transform', open === 'deposits' && 'rotate-180')} />
                  </span>
                ) : null}
              </button>
              <MiniBars data={depTrend} colorClass="bg-mint" />
              <p className="font-inter text-[9px] text-cream-faint uppercase tracking-widest mt-1">
                One bar = one day · hover for the day and amount
              </p>
              {open === 'deposits' && depositsIn.rows.length > 0 ? (
                <ul className="mt-3 space-y-1 border-t border-gold-dim/15 pt-3" data-testid="money-deposits-breakdown">
                  {depositsIn.rows.map((r) => (
                    <li key={r.id} className="flex items-center justify-between text-xs">
                      <span className="font-inter text-cream truncate mr-2">
                        {r.name} <span className="text-cream-faint">· {r.channel}</span>
                      </span>
                      <span className="font-mono text-cream-soft shrink-0">{aud(r.amountAud)}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>

            {/* Held pool (liability snapshot) */}
            <div>
              <p className="label-small-caps flex items-center gap-1">
                Held pool now
                <InfoTip>
                  Every deposit still holding the client&rsquo;s money as of today. Booked in has a live booking;
                  floating is on file with no booking; needs a decision is still linked to a cancelled or no-show
                  booking. A now figure, not the range.
                </InfoTip>
              </p>
              <p className="font-cinzel text-3xl text-cream mt-1">{aud(pool.heldAud)}</p>
              <p className="font-inter text-[10px] text-cream-faint uppercase tracking-widest">
                {pool.count} outstanding
              </p>
              <span className="mt-3 flex h-3 rounded-full overflow-hidden border border-gold-dim/30">
                <span className="bg-gold h-full" style={{ width: `${(pool.bookedInAud / poolTotal) * 100}%` }} />
                <span className="bg-gold-dim h-full" style={{ width: `${(pool.floatingAud / poolTotal) * 100}%` }} />
                <span className="bg-blood/60 h-full" style={{ width: `${(pool.needsDecisionAud / poolTotal) * 100}%` }} />
              </span>
              <div className="mt-2 flex items-center justify-between text-xs">
                <span className="text-cream-soft">
                  Booked in <span className="font-mono text-cream">{aud(pool.bookedInAud)}</span>
                  <span className="text-cream-faint"> · {pool.bookedInCount}</span>
                </span>
                <span className="text-cream-soft text-right">
                  Floating <span className="font-mono text-cream">{aud(pool.floatingAud)}</span>
                  <span className="text-cream-faint"> · {pool.floatingCount}</span>
                </span>
              </div>
              {pool.needsDecisionCount > 0 ? (
                <p className="mt-1 text-xs text-cream-soft">
                  Needs a decision <span className="font-mono text-cream">{aud(pool.needsDecisionAud)}</span>
                  <span className="text-cream-faint"> · {pool.needsDecisionCount}</span>
                </p>
              ) : null}
            </div>
          </div>

          {/* Expiry aging (the held pool by time to its 12-month cutoff) */}
          <div className="mt-5 border-t border-gold-dim/15 pt-4">
            <p className="label-small-caps flex items-center gap-1">
              Expiry aging
              <InfoTip>
                Deposits on file with no date, bucketed by days left of their 12 months. Past 12 months and the nearest
                bucket are the action list: unclaimed, they convert to house revenue.
              </InfoTip>
            </p>
            <div className="mt-2 space-y-1.5">
              {pool.aging.map((a) => {
                const meta = AGING_META[a.key]!;
                return (
                  <div key={a.key} className="flex items-center gap-3">
                    <span className="w-44 text-xs text-cream-soft flex items-center gap-1.5 shrink-0">
                      {meta.label}
                      {meta.flag ? (
                        <span className="text-[9px] uppercase tracking-wider text-blood border border-blood/50 rounded px-1">
                          {meta.flag}
                        </span>
                      ) : null}
                    </span>
                    <span className="flex-1 h-2.5 rounded-full bg-ink-soft/40 overflow-hidden">
                      <span className={clsx('block h-full rounded-full', meta.bar)} style={{ width: `${(a.amountAud / maxBucket) * 100}%` }} />
                    </span>
                    <span className="font-mono text-xs text-cream shrink-0 w-24 text-right">
                      {aud(a.amountAud)} <span className="text-cream-faint">· {a.count}</span>
                    </span>
                  </div>
                );
              })}
            </div>
            {pool.forfeitedInRangeAud > 0 ? (
              <p className="mt-3 text-[11px] text-cream-soft">
                Expired / forfeited in range: <span className="font-mono text-cream">{aud(pool.forfeitedInRangeAud)}</span>{' '}
                <span className="text-cream-faint">· {pool.forfeitedInRangeCount} moved to house</span>
              </p>
            ) : null}
          </div>
        </div>

        {/* Top-ups — the open balance book (a now snapshot, like the pool) */}
        <div className="panel p-4" data-testid="money-topups">
          <h2 className="heading-serif text-lg text-cream mb-1 flex items-center gap-1">
            Top-ups
            <InfoTip>
              Partial deposits with a balance still owing. A now snapshot of the reminder track, not a ranged figure —
              how much is outstanding, how urgent, and how many need a nudge.
            </InfoTip>
          </h2>
          {topups.count === 0 ? (
            <p className="font-serif text-cream-soft text-sm">No top-ups on the book — every deposit is paid in full.</p>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                <DashboardKPITile label="On the book" value={topups.count.toString()} />
                <DashboardKPITile label="Outstanding" value={aud(topups.outstandingAud)} accent />
                <DashboardKPITile label="Close to session" value={topups.closeToSession.toString()} />
                <DashboardKPITile label="Need a nudge" value={topups.needNudge.toString()} />
              </div>

              <p className="label-small-caps mt-5 mb-2">By reminder state</p>
              <div className="flex h-3 rounded overflow-hidden bg-ink-soft/40" data-testid="money-topups-states">
                {TOPUP_STATE_ORDER.map((s) => {
                  const n = topups.byState[s].count;
                  if (n === 0) return null;
                  return (
                    <div
                      key={s}
                      className={TOPUP_STATE_META[s].bar}
                      style={{ width: `${(n / topups.count) * 100}%` }}
                      title={`${TOPUP_STATE_META[s].label}: ${n}`}
                    />
                  );
                })}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                {TOPUP_STATE_ORDER.map((s) => (
                  <span key={s} className="inline-flex items-center gap-1.5 text-[11px] text-cream-soft">
                    <span className={clsx('h-2 w-2 rounded-sm', TOPUP_STATE_META[s].dot)} />
                    {TOPUP_STATE_META[s].label} · {topups.byState[s].count}
                  </span>
                ))}
              </div>

              <p className="label-small-caps mt-5 mb-2">By days to session</p>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-cream-faint">
                    <th className="text-left py-1.5 font-normal">Days to session</th>
                    <th className="text-right py-1.5 font-normal">Count</th>
                    <th className="text-right py-1.5 font-normal">Outstanding</th>
                    <th className="text-left py-1.5 font-normal pl-4">Read</th>
                  </tr>
                </thead>
                <tbody>
                  {topups.byDays.map((b) => (
                    <tr key={b.key} className="border-t border-gold-dim/15" data-testid={`money-topups-days-${b.key}`}>
                      <td className="py-1.5 text-cream">{TOPUP_DAYS_META[b.key]!.label}</td>
                      <td className="py-1.5 text-right font-mono text-cream-soft">{b.count}</td>
                      <td className="py-1.5 text-right font-mono text-cream-soft">{aud(b.amountAud)}</td>
                      <td className={clsx('py-1.5 pl-4', TOPUP_DAYS_META[b.key]!.tone)}>{TOPUP_DAYS_META[b.key]!.read}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>

        {/* Merch */}
        <div className="panel p-4" data-testid="money-merch">
          <h2 className="heading-serif text-lg text-cream mb-1">
            Merch &amp; aftercare
            <InfoTip>
              Merch and aftercare sold in the range (earned, all house, never split with artists), so it sits outside
              turnover. Trend is per day; bars break it down by catalogue category.
            </InfoTip>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-2">
            <div>
              <p className="label-small-caps">Sold in range</p>
              <button
                type="button"
                onClick={() => setOpen(open === 'merch' ? null : 'merch')}
                aria-expanded={open === 'merch'}
                disabled={merch.lines.length === 0}
                className="mt-1 w-full flex items-end justify-between text-left disabled:cursor-default"
              >
                <span className="font-cinzel text-3xl text-gold-bright">{aud(merch.totalAud)}</span>
                {merch.lines.length > 0 ? (
                  <span className="font-inter text-[10px] text-cream-soft uppercase tracking-widest flex items-center gap-1">
                    {merch.lines.reduce((s, l) => s + l.qty, 0)} items
                    <ChevronDown size={12} className={clsx('transition-transform', open === 'merch' && 'rotate-180')} />
                  </span>
                ) : null}
              </button>
              <MiniBars data={merchTrend} colorClass="bg-gold" />
              {open === 'merch' && merch.lines.length > 0 ? (
                <ul className="mt-3 space-y-1 border-t border-gold-dim/15 pt-3" data-testid="money-merch-breakdown">
                  {merch.lines.map((l) => (
                    <li key={l.itemId} className="flex items-center justify-between text-xs">
                      <span className="font-inter text-cream truncate mr-2">
                        {l.name} <span className="text-cream-faint">· {l.qty}× {l.category}</span>
                      </span>
                      <span className="font-mono text-cream-soft shrink-0">{aud(l.totalAud)}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
            <div>
              <p className="label-small-caps">By category</p>
              <div className="mt-2 space-y-2">
                {merchCats.length === 0 ? (
                  <p className="text-xs text-cream-faint">No merch sold in this range.</p>
                ) : (
                  merchCats.map((c) => (
                    <div key={c.category} className="flex items-center gap-3">
                      <span className="w-24 text-xs text-cream-soft capitalize shrink-0">{c.category}</span>
                      <span className="flex-1 h-2.5 rounded-full bg-ink-soft/40 overflow-hidden">
                        <span className="block h-full rounded-full bg-gold" style={{ width: `${(c.totalAud / maxCat) * 100}%` }} />
                      </span>
                      <span className="font-mono text-xs text-cream shrink-0 w-20 text-right">{aud(c.totalAud)}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Revenue per day, stacked ours vs artist */}
      <section className={clsx('panel p-4', flashing && 'report-flash')} data-testid="money-revenue-split">
        <h2 className="heading-serif text-lg text-cream mb-1">
          Revenue, ours vs artist
          <InfoTip>
            Completed-session gross (sale balance + deposit applied, merch excluded) per day over the selected range,
            split into the house share and the artist envelope by the commission engine.
          </InfoTip>
        </h2>
        <p className="font-inter text-[10px] text-cream-faint uppercase tracking-widest mb-3">
          Completed-session gross per day, split via the commission engine
        </p>
        <ReportBarChart
          data={revenueTrend}
          xKey="day"
          stacked
          series={[
            { key: 'Ours', name: 'Ours (house)', color: REPORT_COLORS.gold },
            { key: 'Artist', name: 'Artist', color: REPORT_COLORS.azure },
          ]}
        />
      </section>

      {/* Per-store totals (all studios) or per-artist (single studio) */}
      <section className="panel p-4" data-testid="money-per-store">
        <h2 className="heading-serif text-lg text-cream mb-3">
          {studioId ? 'Per-artist totals' : 'Per-store totals'}
          <InfoTip>
            {studioId
              ? "Each artist's completed-session revenue for this studio over the range - turnover, the house share and their envelope. Shown instead of the single-studio row so it adds detail the chart can't."
              : "Each studio's completed-session revenue over the range - turnover, the house share and the artist envelope. Largest turnover first. Filter to one studio to see it broken down per artist."}
          </InfoTip>
        </h2>
        <div className="overflow-x-auto">
          {studioId ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-cream-soft">
                  <th className="text-left px-2 py-2">Artist</th>
                  <th className="text-right px-2 py-2">Sessions</th>
                  <th className="text-right px-2 py-2">Turnover</th>
                  <th className="text-right px-2 py-2">Ours</th>
                  <th className="text-right px-2 py-2">Artist</th>
                </tr>
              </thead>
              <tbody>
                {perArtist.length === 0 ? (
                  <tr>
                    <td className="px-2 py-3 text-cream-faint" colSpan={5}>
                      No completed sessions for this studio in the range.
                    </td>
                  </tr>
                ) : (
                  perArtist.map((a) => (
                    <tr key={a.artistId} className="border-t border-gold-dim/15" data-testid={`money-artist-${a.artistId}`}>
                      <td className="px-2 py-2 font-inter text-cream whitespace-nowrap">{a.name}</td>
                      <td className="px-2 py-2 text-right font-mono text-cream-soft">{a.sessions}</td>
                      <td className="px-2 py-2 text-right font-mono text-cream">{aud(a.totalAud)}</td>
                      <td className="px-2 py-2 text-right font-mono text-gold-bright">{aud(a.oursAud)}</td>
                      <td className="px-2 py-2 text-right font-mono text-cream-soft">{aud(a.artistAud)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-cream-soft">
                  <th className="text-left px-2 py-2">Studio</th>
                  <th className="text-right px-2 py-2">Turnover</th>
                  <th className="text-right px-2 py-2">Ours</th>
                  <th className="text-right px-2 py-2">Artists</th>
                </tr>
              </thead>
              <tbody>
                {perStore.map((s) => (
                  <tr key={s.studioId} className="border-t border-gold-dim/15" data-testid={`money-store-${s.studioId}`}>
                    <td className="px-2 py-2 font-inter text-cream whitespace-nowrap">{s.name}</td>
                    <td className="px-2 py-2 text-right font-mono text-cream">{aud(s.totalAud)}</td>
                    <td className="px-2 py-2 text-right font-mono text-gold-bright">{aud(s.oursAud)}</td>
                    <td className="px-2 py-2 text-right font-mono text-cream-soft">{aud(s.artistAud)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-gold-dim/30 text-cream">
                  <td className="px-2 py-2 label-small-caps text-gold-bright">All studios</td>
                  <td className="px-2 py-2 text-right font-mono">{aud(perStore.reduce((s, r) => s + r.totalAud, 0))}</td>
                  <td className="px-2 py-2 text-right font-mono text-gold-bright">{aud(perStore.reduce((s, r) => s + r.oursAud, 0))}</td>
                  <td className="px-2 py-2 text-right font-mono text-cream-soft">{aud(perStore.reduce((s, r) => s + r.artistAud, 0))}</td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </section>

      {/* Cash timing: a fixed rolling 7-day settlement view, independent of the range */}
      {hasTiming ? (
        <section className="panel p-4" data-testid="money-cash-timing">
          <h2 className="heading-serif text-lg text-cream mb-1">
            Cash timing
            <InfoTip align="right">
              Card takings earned per day vs the settlement lumps landing in the bank that day. The bank does not
              settle on weekends, so Friday/Saturday takings land Monday/Tuesday - the weekend gap. Summed across studios.
            </InfoTip>
          </h2>
          <p className="font-inter text-[10px] text-cream-faint uppercase tracking-widest mb-3">
            Card takings earned vs settlement landing - the weekend gap, honestly shown
          </p>
          <ReportBarChart
            data={timingChart}
            xKey="day"
            series={[
              { key: 'Takings', name: 'Takings (earned)', color: REPORT_COLORS.gold },
              { key: 'Landed', name: 'Landed in bank', color: REPORT_COLORS.azure },
            ]}
          />
          <p className="mt-3 text-[11px] text-cream-soft border-l-2 border-gold-dim/50 pl-3">
            A fixed rolling 7-day settlement view, independent of the range above - it answers when the money lands,
            not revenue over the range.
          </p>
        </section>
      ) : null}

      {/* Deep links to the detailed finance screens */}
      <section className="flex flex-wrap gap-3" data-testid="money-deep-links">
        <Link to="/calculations" className="btn-quiet text-sm" data-testid="money-link-calculations">
          Open Day sheet <ArrowUpRight size={14} />
        </Link>
        <Link to="/reconciliation" className="btn-quiet text-sm" data-testid="money-link-reconciliation">
          Open Reconcile <ArrowUpRight size={14} />
        </Link>
      </section>
    </div>
  );
}

/** A compact per-day trend of small bars, scaled to the window max. Decorative; each bar titled. */
function MiniBars({ data, colorClass }: { data: readonly { dayIso: string; amountAud: number }[]; colorClass: string }) {
  const max = Math.max(1, ...data.map((d) => d.amountAud));
  return (
    <div className="mt-3 flex items-end gap-1 h-12">
      {data.map((d) => (
        <div key={d.dayIso} className="flex-1 flex items-end h-full" title={`${dayLabel(d.dayIso)}: ${aud(d.amountAud)}`}>
          <div
            className={clsx('w-full rounded-t opacity-80', colorClass)}
            style={{ height: `${Math.max(3, (d.amountAud / max) * 100)}%` }}
          />
        </div>
      ))}
    </div>
  );
}
